import React, { useEffect, useState } from 'react';
import { local_socket_api } from '../config';
import { FaEyeSlash } from 'react-icons/fa';

const SparklesIcon = ({ className }) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
        <path d="M10 0C10 5.52285 5.52285 10 0 10C5.52285 10 10 14.4772 10 20C10 14.4772 14.4772 10 20 10C14.4772 10 10 5.52285 10 0Z" fill="url(#paint0_linear)" />
        <path d="M20 3C20 4.65685 18.6569 6 17 6C18.6569 6 20 7.34315 20 9C20 7.34315 21.3431 6 23 6C21.3431 6 20 4.65685 20 3Z" fill="#F472B6" />
        <path d="M4 16C4 16.5523 3.55228 17 3 17C3.55228 17 4 17.4477 4 18C4 17.4477 4.44772 17 5 17C4.44772 17 4 16.5523 4 16Z" fill="#A78BFA" />
        <defs>
            <linearGradient id="paint0_linear" x1="10" y1="0" x2="10" y2="20" gradientUnits="userSpaceOnUse">
                <stop stopColor="#A855F7" />
                <stop offset="1" stopColor="#EC4899" />
            </linearGradient>
        </defs>
    </svg>
);


const UploadProgressTracker = ({ runId, onClose }) => {
    const [progressData, setProgressData] = useState(null);
    const [retry, setRetry] = useState(0);
    const [terminalState, setTerminalState] = useState(null);

    useEffect(() => {
        if (!runId) return;

        const ws = new WebSocket(`${local_socket_api}/sourcing-progress?run_id=${runId}`);

        ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);

                if (data.type === 'error') {
                    console.log("Progress tracker error, retrying in 3s...");
                    setTimeout(() => setRetry(prev => prev + 1), 3000);
                    ws.close();
                    return;
                }

                setProgressData(data.data);

                const currentStatus = data?.data?.status || data?.status;
                if (currentStatus === 'completed' || currentStatus === 'failed') {
                    setTerminalState(currentStatus);
                    ws.close();
                }
            } catch (e) {
                console.error("Error parsing websocket message", e);
            }
        };

        return () => {
            ws.close();
        };
    }, [onClose, runId, retry]);

    useEffect(() => {
        if (!terminalState) return;
        const delay = terminalState === 'completed' ? 3000 : 4000;
        const timer = setTimeout(() => onClose(), delay);
        return () => clearTimeout(timer);
    }, [terminalState, onClose]);

    if (!progressData) return null;

    const isFailed = terminalState === 'failed';
    const isCompleted = terminalState === 'completed';
    const isParsing = progressData.stage === 'processing_resumes';
    const stages = progressData.stages || [];

    const headerText = () => {
        if (isCompleted) return 'Sourcing Complete!';
        if (isFailed) return 'Sourcing Failed';
        if (isParsing) {
            return `Parsing ${progressData.resumes_processed || 0}/${progressData.resumes_total || 0} Resumes`;
        }
        if (progressData.current_job) {
            return `Scoring: ${progressData.current_job}`;
        }
        return `Assigning ${progressData.resumes_total || 0} profiles to best match jobs`;
    };

    const subtitleText = () => {
        if (isCompleted) {
            return `${progressData.total_candidates_scored || 0} candidates scored across ${progressData.jobs_completed || 0} jobs`;
        }
        if (isFailed) return 'An error occurred during processing';
        return null;
    };

    const percentColor = isFailed ? 'text-red-400' : isCompleted ? 'text-emerald-400' : 'text-[#00E58F]';

    return (
        <div className="fixed bottom-10 left-1/2 transform -translate-x-1/2 z-50 bg-[#111827] px-5 py-4 rounded-xl shadow-2xl w-[500px] max-w-[90vw] border border-gray-800 flex items-start gap-4">
            <div className="flex items-center gap-4 flex-1 min-w-0">
                {isParsing && !isCompleted && !isFailed ? (
                    <div className="relative flex-shrink-0 flex justify-center items-center w-10 h-10">
                        <svg className="w-10 h-10 transform -rotate-90">
                            <circle cx="20" cy="20" r="16" stroke="currentColor" strokeWidth="4" fill="transparent" className="text-gray-700" />
                            <circle cx="20" cy="20" r="16" stroke="currentColor" strokeWidth="4" fill="transparent"
                                strokeDasharray={16 * 2 * Math.PI}
                                strokeDashoffset={(16 * 2 * Math.PI) - (progressData.resumes_processed / progressData.resumes_total) * (16 * 2 * Math.PI)}
                                className="text-blue-500 transition-all duration-300"
                                strokeLinecap="round"
                            />
                        </svg>
                    </div>
                ) : isCompleted ? (
                    <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-emerald-500/20">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M20 6L9 17L4 12" stroke="#10B981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                ) : isFailed ? (
                    <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-red-500/20">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M18 6L6 18M6 6L18 18" stroke="#EF4444" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                ) : (
                    <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
                        <SparklesIcon className="w-8 h-8" />
                    </div>
                )}

                <div className="flex flex-col gap-1 text-left flex-1 min-w-0">
                    <span className="text-white font-bold text-[16px] tracking-wide truncate">
                        {headerText()}
                    </span>
                    {subtitleText() ? (
                        <span className="text-gray-400 text-sm truncate">{subtitleText()}</span>
                    ) : (
                        <span className={`${percentColor} font-semibold text-[13px] tracking-wide truncate`}>
                            {progressData.percentage_complete}% Completed
                            <span className="text-gray-400 font-medium px-2">|</span>
                            <span className="text-gray-300 font-medium">Few mins left</span>
                        </span>
                    )}
                </div>
            </div>

            <button
                onClick={onClose}
                className="text-gray-400 flex-shrink-0 hover:text-white p-1 rounded-md hover:bg-gray-800 transition-colors self-center"
                title="Hide Tracker"
            >
                <FaEyeSlash className="w-5 h-5" />
            </button>
        </div>
    );
};

export default UploadProgressTracker;
